import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { apiFetch } from '../utils/api';
import FormModal from '../components/FormModal';
import ConfirmDialog from '../components/ConfirmDialog';

export default function Tags() {
  const { token } = useAuth();
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [showCreate, setShowCreate] = useState(false);
  const [deleteItem, setDeleteItem] = useState(null);
  const [saving, setSaving] = useState(false);
  const [newName, setNewName] = useState('');

  const fetchTags = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await apiFetch('/tags', token);
      setTags(data.items);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchTags(); }, [fetchTags]);

  async function handleCreate() {
    setSaving(true);
    try {
      await apiFetch('/tags', token, { method: 'POST', body: { name: newName } });
      setShowCreate(false);
      setNewName('');
      fetchTags();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    setSaving(true);
    try {
      await apiFetch(`/tags/${encodeURIComponent(deleteItem.name)}`, token, { method: 'DELETE' });
      setDeleteItem(null);
      fetchTags();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div>
      {error && <div className="alert alert-danger alert-dismissible py-1 mb-2">
        <small>{error}</small>
        <button type="button" className="btn-close btn-close-sm" style={{ padding: '0.5rem' }} onClick={() => setError('')} />
      </div>}

      <div className="d-flex justify-content-between align-items-center mb-2">
        <small className="text-muted">{tags.length} tag{tags.length !== 1 ? 's' : ''}</small>
        <button className="btn btn-sm btn-primary" onClick={() => { setNewName(''); setShowCreate(true); }}>New Tag</button>
      </div>

      {loading ? (
        <div className="text-center py-4"><div className="spinner-border spinner-border-sm" /></div>
      ) : tags.length === 0 ? (
        <p className="text-muted text-center py-4">No tags yet. Create one to get started.</p>
      ) : (
        <div className="table-responsive">
          <table className="table table-sm table-hover align-middle">
            <thead className="table-light">
              <tr>
                <th>Tag Name</th>
                <th>Created</th>
                <th style={{ width: '1%' }}></th>
              </tr>
            </thead>
            <tbody>
              {tags.map(t => (
                <tr key={t.name}>
                  <td><span className="badge bg-info text-dark">{t.name}</span></td>
                  <td><small>{new Date(t.createdAt).toLocaleDateString()}</small></td>
                  <td>
                    <button className="btn btn-sm btn-outline-danger py-0 px-1" onClick={() => setDeleteItem(t)} title="Delete">&times;</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showCreate && (
        <FormModal title="New Tag" onClose={() => setShowCreate(false)} onSubmit={handleCreate} loading={saving} size="modal-sm">
          <div className="mb-2">
            <label className="form-label small mb-1">Tag Name</label>
            <input className="form-control form-control-sm" value={newName}
              onChange={e => setNewName(e.target.value)} required autoFocus />
          </div>
        </FormModal>
      )}

      {deleteItem && (
        <ConfirmDialog
          title="Delete Tag"
          message={`Delete tag "${deleteItem.name}"? It will be removed from all requests.`}
          confirmLabel="Delete"
          onConfirm={handleDelete}
          onCancel={() => setDeleteItem(null)}
          loading={saving}
        />
      )}
    </div>
  );
}
